/**
 * RegionContext — which regional silo the SPA is currently viewing.
 *
 * The platform-admin SPA is identical in every region. Switching region in
 * the chip changes the base URL of every API call (see ``regionStore``) so
 * the same UI can browse EU data or US data without page reload.
 *
 * Single-region today (only EU). The chip in Sidebar renders as a static
 * label. When a second entry is added to ``REGIONS``, the chip auto-promotes
 * to a DropdownSelect.
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { setApiBaseUrl } from '../services/regionStore';

export type RegionCode = 'eu' | 'us';

export interface Region {
    code: RegionCode;
    label: string;
    /** Empty string = same-origin (single-region mode). Otherwise absolute URL of that region's admin backend. */
    apiBaseUrl: string;
}

// Non-empty tuple type so REGIONS[0] is `Region` (not `Region | undefined`
// under noUncheckedIndexedAccess) — there is always at least the EU silo.
export const REGIONS: [Region, ...Region[]] = [
    { code: 'eu', label: 'Europa', apiBaseUrl: '' },
    // Activate when US silo is online:
    // { code: 'us', label: 'Estados Unidos', apiBaseUrl: 'https://admin.us.bolloprint.com' },
];

const STORAGE_KEY = 'bp_admin_region';

interface RegionContextType {
    regions: Region[];
    currentRegion: Region;
    setRegion: (code: RegionCode) => void;
    /** True when more than one region exists — chip becomes interactive. */
    isMultiRegion: boolean;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

function resolveInitialRegion(): Region {
    if (typeof window !== 'undefined') {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const match = REGIONS.find((r) => r.code === saved);
            if (match) return match;
        }
    }
    return REGIONS[0];
}

export const RegionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentRegion, setCurrentRegion] = useState<Region>(() => resolveInitialRegion());

    useEffect(() => {
        setApiBaseUrl(currentRegion.apiBaseUrl);
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(STORAGE_KEY, currentRegion.code);
        }
    }, [currentRegion]);

    const setRegion = (code: RegionCode) => {
        const next = REGIONS.find((r) => r.code === code);
        if (next) setCurrentRegion(next);
    };

    const value = useMemo<RegionContextType>(
        () => ({
            regions: REGIONS,
            currentRegion,
            setRegion,
            isMultiRegion: REGIONS.length > 1,
        }),
        [currentRegion],
    );

    return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};

export const useRegion = (): RegionContextType => {
    const ctx = useContext(RegionContext);
    if (!ctx) throw new Error('useRegion must be used within a RegionProvider');
    return ctx;
};
